<template>
  <ButtonMenu
    class="button-menu-recurrence"
    placement="bottom"
    :is-disabled="isDisabled"
  >
    <template #btn>
      <Recurrence :value="recurrence" :disabled="isDisabled" />
    </template>
    <template #content>
      <BaseButton
        v-if="recurrence"
        kind="auxiliary"
        text="Do not repeat"
        is-expanded
        @click="editRecurrence('no-repeat')"
      />
      <BaseButton
        kind="auxiliary"
        text="Every day"
        is-expanded
        @click="editRecurrence('day')"
      />
      <BaseButton
        kind="auxiliary"
        text="Every workday"
        is-expanded
        @click="editRecurrence('workday')"
      />
      <BaseButton
        kind="auxiliary"
        :text="`Every ${format(date, 'EEEE')}`"
        is-expanded
        @click="editRecurrence('week')"
      />
      <BaseButton
        kind="auxiliary"
        :text="`Monthly on the ${format(date, 'do')}`"
        is-expanded
        @click="editRecurrence('month')"
      />
      <BaseButton
        kind="auxiliary"
        :text="`Anually on ${format(date, 'MMM do')}`"
        is-expanded
        @click="editRecurrence('year')"
      />
      <BaseButton
        v-if="isCustomDisplayed"
        kind="auxiliary"
        text="Custom…"
        is-expanded
        @click="$emit('custom')"
      />
    </template>
  </ButtonMenu>
</template>
<script lang="ts">
import { format, getDate, getISODay } from "date-fns";
import { computed, defineComponent, PropType, toRefs } from "vue";

import type {
  EventTime,
  Recurrence as RecurrenceType,
} from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import { getMonth } from "@/components/dates";
import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import Recurrence from "@/components/Recurrence.vue";

export default defineComponent({
  components: { BaseButton, Recurrence, ButtonMenu },
  props: {
    isDisabled: { type: Boolean, required: false },
    recurrence: {
      type: Object as PropType<RecurrenceType | Array<string>>,
      required: false,
    },
    time: { type: Object as PropType<EventTime>, required: true },
  },
  emits: {
    "update:recurrence": (_: RecurrenceType | undefined) => true,
    custom: () => true,
  },
  setup(props, { emit }) {
    const { recurrence, time } = toRefs(props);
    const date = computed<Date>(() =>
      "start_time" in time.value
        ? time.value.start_time
        : "start_date" in time.value
          ? time.value.start_date
          : time.value.date
    );
    const editRecurrence = (
      type: "day" | "week" | "workday" | "month" | "year" | "no-repeat"
    ) => {
      const day = getDate(date.value);
      let recurrence: RecurrenceType | undefined;
      switch (type) {
        case "day":
          recurrence = { rule: { every: "day" } };
          break;
        case "workday":
          recurrence = {
            rule: {
              every: "week",
              weekdays: {
                monday: true,
                tuesday: true,
                wednesday: true,
                thursday: true,
                friday: true,
              },
            },
          };
          break;
        case "week":
          {
            const isoDay = getISODay(date.value);
            recurrence = {
              rule: {
                every: "week",
                weekdays: {
                  monday: isoDay === 1,
                  tuesday: isoDay === 2,
                  wednesday: isoDay === 3,
                  thursday: isoDay === 4,
                  friday: isoDay === 5,
                  saturday: isoDay === 6,
                  sunday: isoDay === 7,
                },
              },
            };
          }
          break;
        case "month":
          recurrence = {
            rule: {
              every: "month",
              day,
            },
          };
          break;
        case "year":
          recurrence = {
            rule: { every: "year", day, month: getMonth(date.value) },
          };
          break;
        case "no-repeat":
          recurrence = undefined;
          break;
      }
      emit("update:recurrence", recurrence);
    };
    const isCustomDisplayed = computed<boolean>(
      () => !Array.isArray(recurrence.value)
    );
    return { editRecurrence, format, date, isCustomDisplayed };
  },
});
</script>
